import { type SizeGuideDimensionsUnit } from "@tablescape/state/productSizeGuide";
import {
  type SizeGuideCard,
  type SizeGuideContextItem,
  type SizeGuideContent,
  type SizeGuideGraphQLCardNode,
  type SizeGuideGraphQLContextItemNode,
  type SizeGuideGraphQLFileReference,
  type SizeGuideGraphQLGuideNode,
  type SizeGuideGraphQLSpecNode,
  type SizeGuideMedia,
  type SizeGuideSpec,
  type SizeGuideTableRow,
} from "@tablescape/state/sizeGuide";
import {
  checkIfHeightString,
  getInchValueFromHeightString,
} from "@tablescape/utilities/general";

const EMPTY_PLACEHOLDER = "—";

const hasText = (value?: string | null): value is string =>
  typeof value === "string" && value.trim().length > 0;

export const formatSizeGuideMediaReference = (
  reference?: SizeGuideGraphQLFileReference
): SizeGuideMedia | undefined => {
  if (!reference) {
    return undefined;
  }

  if ("image" in reference && reference.image?.url) {
    return {
      alt: reference.alt ?? undefined,
      height: reference.image.height ?? undefined,
      url: reference.image.url,
      width: reference.image.width ?? undefined,
    };
  }

  if ("url" in reference && reference.url) {
    return { url: reference.url };
  }

  return undefined;
};

const formatSizeGuideSpec = (
  node?: SizeGuideGraphQLSpecNode | null
): SizeGuideSpec | undefined => {
  const label = node?.label?.value;
  const value = node?.value?.value;

  if (!hasText(label) || !hasText(value)) {
    return undefined;
  }

  return {
    label,
    value,
    supportsInCmConversion: node?.supportsInCmConversion?.value === "true",
  };
};

const formatSizeGuideCard = (
  node?: SizeGuideGraphQLCardNode | null
): SizeGuideCard | undefined => {
  const label = node?.label?.value;

  if (!hasText(label)) {
    return undefined;
  }

  const specs =
    node?.specs?.references?.nodes
      ?.map(formatSizeGuideSpec)
      .filter((spec): spec is SizeGuideSpec => spec != null) ?? [];

  return {
    caption: hasText(node?.caption?.value) ? node.caption.value : undefined,
    exteriorImage: formatSizeGuideMediaReference(
      node?.exteriorImage?.reference
    ),
    interiorImage: formatSizeGuideMediaReference(
      node?.interiorImage?.reference
    ),
    label,
    specs,
  };
};

const formatSizeGuideContextItem = (
  node?: SizeGuideGraphQLContextItemNode | null
): SizeGuideContextItem | undefined => {
  const text = node?.text?.value;

  if (!hasText(text)) {
    return undefined;
  }

  return {
    icon: formatSizeGuideMediaReference(node?.icon?.reference),
    text,
  };
};

export const buildSizeGuideTableRows = (
  cards: SizeGuideCard[]
): SizeGuideTableRow[] => {
  const firstCard = cards[0];

  if (!firstCard) {
    return [];
  }

  return firstCard.specs.map((rowSpec) => ({
    label: rowSpec.label,
    supportsInCmConversion: rowSpec.supportsInCmConversion,
    values: cards.map((card) => {
      const match = card.specs.find((spec) => spec.label === rowSpec.label);
      return match?.value ?? EMPTY_PLACEHOLDER;
    }),
  }));
};

export const resolveSizeGuideConvertibleValue = (
  value: string,
  unit: SizeGuideDimensionsUnit
): string => {
  const rangeSeparator = value.match(/[-–]/)?.[0];

  if (rangeSeparator) {
    return value
      .split(rangeSeparator)
      .map((part) => resolveSizeGuideConvertibleValue(part.trim(), unit))
      .join(rangeSeparator);
  }

  const inchValue = checkIfHeightString(value)
    ? getInchValueFromHeightString(value)
    : Number(value);

  if (unit === "in" || Number.isNaN(inchValue)) {
    return value;
  }

  const unitSuffix = checkIfHeightString(value) && unit === "cm" ? "cm" : "";

  return `${(inchValue * 2.54).toFixed(1)}${unitSuffix}`;
};

export const formatSizeGuideDisplayValue = (
  value: string,
  supportsInCmConversion: boolean,
  unit: SizeGuideDimensionsUnit
) => {
  if (value === EMPTY_PLACEHOLDER) {
    return value;
  }

  if (!supportsInCmConversion) {
    return value;
  }

  return resolveSizeGuideConvertibleValue(value, unit);
};

export const formatSizeGuideContent = (
  guide?: SizeGuideGraphQLGuideNode | null
): SizeGuideContent | null => {
  if (!guide?.id || !hasText(guide.title?.value)) {
    return null;
  }

  const sizeCards =
    guide.sizeCards?.references?.nodes
      ?.map(formatSizeGuideCard)
      .filter((card): card is SizeGuideCard => card != null) ?? [];

  if (sizeCards.length === 0) {
    return null;
  }

  const contextItems =
    guide.contextItems?.references?.nodes
      ?.map(formatSizeGuideContextItem)
      .filter((item): item is SizeGuideContextItem => item != null) ?? [];

  return {
    contextHeading: hasText(guide.contextHeading?.value)
      ? guide.contextHeading.value
      : undefined,
    contextItems,
    framingCopy: hasText(guide.framingCopy?.value)
      ? guide.framingCopy.value
      : undefined,
    guidingQuestion: hasText(guide.guidingQuestion?.value)
      ? guide.guidingQuestion.value
      : undefined,
    id: guide.id,
    recommendationCopy: hasText(guide.recommendationCopy?.value)
      ? guide.recommendationCopy.value
      : undefined,
    seeInsideLabel: hasText(guide.seeInsideLabel?.value)
      ? guide.seeInsideLabel.value
      : undefined,
    sizeCards,
    specTableTitle: hasText(guide.specTableTitle?.value)
      ? guide.specTableTitle.value
      : undefined,
    tableRows: buildSizeGuideTableRows(sizeCards),
    title: guide.title.value,
  };
};
